import type { ModuleSpecBlock } from '../data/moduleSpecContracts';
import type { ModuleSpecDraftLayout } from '../data/contracts';
import {canonicalJson} from './specChecksum';
export type ReviewedBlock = Omit<ModuleSpecBlock, 'children'> & {
  review?: NonNullable<ModuleSpecDraftLayout['screenReviews']>[string]; children?: ReviewedBlock[];
};

export function movePageReviews(layout: ModuleSpecDraftLayout, previous: string, next?: string): ModuleSpecDraftLayout {
  if(!layout.screenReviews)return layout;
  const reviews: NonNullable<ModuleSpecDraftLayout['screenReviews']> = {};
  for(const [key,value] of Object.entries(layout.screenReviews)) {
    if(!key.startsWith(previous+'/'))reviews[key]=value;
    else if(next)reviews[next+key.slice(previous.length)]=value;
  }
  return {...layout,screenReviews:reviews};
}

export function attachScreenReviews(blocks: ModuleSpecBlock[], reviews: ModuleSpecDraftLayout['screenReviews'], prefix: string): ReviewedBlock[] {
  return blocks.map(block => {
    const review=reviews?.[`${prefix}/${block.key}`];
    const stale=review && ['accepted','rejected'].includes(review.status) && (!review.content || review.content!==reviewIdentity(block));
    return { ...block, review: review ? {...review,status:stale?'edited':review.status} : undefined,
    ...(block.children ? { children: attachScreenReviews(block.children, reviews, `${prefix}/${block.key}`) } : {}) };
  });
}

export function collectScreenReviews(blocks: ReviewedBlock[], prefix: string, reviews: NonNullable<ModuleSpecDraftLayout['screenReviews']> = {}): NonNullable<ModuleSpecDraftLayout['screenReviews']> {
  for (const block of blocks) {
    const key = `${prefix}/${block.key}`;
    if (block.review) reviews[key] = block.review;
    if (block.children) collectScreenReviews(block.children, key, reviews);
  }
  return reviews;
}

/** Le témoin ne contient jamais d'autre témoin : taille bornée, même après plusieurs retouches. */
export function reviewContent(block: ReviewedBlock): ModuleSpecBlock {
  const content = { ...block };
  delete content.review;
  return { ...content, ...(content.children ? { children: content.children.map(reviewContent) } : {}) };
}

export function reviewBlock(block: ReviewedBlock, status: NonNullable<ReviewedBlock['review']>['status'], patch: Partial<ModuleSpecBlock> = {}): ReviewedBlock {
  const before = block.review?.before ?? JSON.stringify(reviewContent(block));
  const next = status === 'rejected'
    ? { key: block.key, type: 'custom' as const, title: block.title, box: block.box, mobile: block.mobile,
        captureRef: block.captureRef, prototypeRef: block.prototypeRef, reason: 'low_confidence' as const }
    : { ...block, ...patch };
  const clean=JSON.parse(JSON.stringify(next)) as ReviewedBlock;
  delete clean.review;
  return { ...clean, review: { status, before, content:reviewIdentity(clean) } };
}
function reviewIdentity(block:ReviewedBlock):string{return canonicalJson(JSON.parse(JSON.stringify(reviewContent(block))));}

export interface ScreenDifference { path: string; before: unknown; after: unknown }
/** Différence récursive de valeurs, y compris suppressions, ordre et propriétés imbriquées. */
export function screenDifferences(before: unknown, after: unknown, path = ''): ScreenDifference[] {
  if (JSON.stringify(before) === JSON.stringify(after)) return [];
  if (before !== null && after !== null && typeof before === 'object' && typeof after === 'object') {
    const left = before as Record<string, unknown>, right = after as Record<string, unknown>;
    return [...new Set([...Object.keys(left), ...Object.keys(right)])].sort().flatMap(key =>
      screenDifferences(left[key], right[key], `${path}/${key.replace(/~/g, '~0').replace(/\//g, '~1')}`));
  }
  return [{ path, before, after }];
}

export function reviewBefore(block: ReviewedBlock): unknown {
  try { return block.review ? JSON.parse(block.review.before) : reviewContent(block); }
  catch { return null; }
}
